import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Download, RefreshCw, CheckCircle, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ExportButton — supports two export patterns:
 *
 * Pattern A — background job (for large exports):
 *   requestUrl    — e.g. "/api/reports/allup/export/request"
 *   params        — current filter params object
 *   statusBaseUrl — base path for status/download polling (default: "/api/reports")
 *
 * Pattern B — direct fetch + client-side CSV (for moderate datasets):
 *   fetchAllFn  — async function that returns data array
 *   columns     — [{key, label}] column definitions
 *
 * Shared props:
 *   filename    — download filename (without .csv)
 *   disabled    — optional bool
 *   testId      — optional data-testid override
 *   label       — optional button label override
 */
export function ExportButton({ requestUrl, params = {}, statusBaseUrl = '/api/reports', fetchAllFn, columns, filename = 'report.csv', disabled = false, testId = 'export-csv-btn', label }) {
  const [phase, setPhase] = useState('idle'); // idle | requesting | polling | done | error
  const [jobId, setJobId] = useState(null);
  const [dlFilename, setDlFilename] = useState(filename);
  const [errorMsg, setErrorMsg] = useState('');
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => () => stopPolling(), []);

  const triggerDownload = async (jId, fname) => {
    try {
      const r = await axios.get(`${API}${statusBaseUrl}/export/download/${jId}`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = fname || filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed', e);
    }
  };

  // ── Pattern B: direct fetch + client-side CSV ─────────────────────────────
  const startExportDirect = async () => {
    setPhase('requesting');
    setErrorMsg('');
    try {
      const data = await fetchAllFn();
      const cols = columns || (data.length > 0 ? Object.keys(data[0]).map(k => ({ key: k, label: k })) : []);
      const header = cols.map(c => `"${(c.label || c.key).replace(/"/g, '""')}"`).join(',');
      const rows = data.map(row =>
        cols.map(c => {
          const v = row[c.key];
          if (v === null || v === undefined) return '';
          const s = String(v).replace(/"/g, '""');
          return `"${s}"`;
        }).join(',')
      );
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`; a.click();
      URL.revokeObjectURL(url);
      setPhase('done');
      setTimeout(() => setPhase('idle'), 4000);
    } catch (e) {
      console.error('Export failed', e);
      setPhase('error');
      setErrorMsg('Export failed');
    }
  };

  const startExport = async () => {
    if (fetchAllFn) {
      return startExportDirect();
    }
    stopPolling();
    setPhase('requesting');
    setErrorMsg('');
    try {
      const r = await axios.get(`${API}${requestUrl}`, { params, withCredentials: true });
      const { job_id } = r.data;
      setJobId(job_id);
      setPhase('polling');
      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}${statusBaseUrl}/export/status/${job_id}`, { withCredentials: true });
          const { status, filename: fname, error } = s.data;
          if (status === 'done') {
            stopPolling();
            setDlFilename(fname || filename);
            setPhase('done');
            await triggerDownload(job_id, fname || filename);
            setTimeout(() => setPhase('idle'), 4000);
          } else if (status === 'error') {
            stopPolling();
            setPhase('error');
            setErrorMsg(error || 'Export failed');
          }
        } catch {
          stopPolling();
          setPhase('error');
          setErrorMsg('Status check failed');
        }
      }, 3000);
    } catch {
      setPhase('error');
      setErrorMsg('Failed to start export');
    }
  };

  if (phase === 'idle') {
    return (
      <button
        className="btn btn-secondary"
        onClick={startExport}
        disabled={disabled}
        data-testid={testId}
        title="Export all records to CSV"
      >
        <Download size={13} /> {label || 'CSV'}
      </button>
    );
  }
  if (phase === 'requesting') {
    return (
      <button className="btn btn-secondary" disabled data-testid={testId}>
        <RefreshCw size={13} className="spinning" /> Requesting…
      </button>
    );
  }
  if (phase === 'polling') {
    return (
      <button className="btn btn-secondary" disabled data-testid={testId} title="Generating CSV in background…">
        <RefreshCw size={13} className="spinning" /> Generating…
      </button>
    );
  }
  if (phase === 'done') {
    return (
      <button
        className="btn"
        style={{ background: '#16a34a', color: 'white', border: 'none', cursor: 'pointer' }}
        onClick={() => triggerDownload(jobId, dlFilename)}
        data-testid="export-download-btn"
        title="Click to download again"
      >
        <CheckCircle size={13} /> Download Ready
      </button>
    );
  }
  // error
  return (
    <button
      className="btn btn-secondary"
      onClick={startExport}
      style={{ color: '#dc2626' }}
      data-testid={testId}
      title={errorMsg || 'Export failed — click to retry'}
    >
      <XCircle size={13} /> Retry
    </button>
  );
}
